import React, { useEffect, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { api } from "@/lib/api";
import { formatPrice } from "@/components/ListingCard";
import { Plus, X } from "lucide-react";

const CRMListings = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "residential", price: "", location: "", address: "",
    bedrooms: "", bathrooms: "", sqft: "", description: "",
    images: "", featured: false, status: "active",
  });

  const load = () => api.get("/crm/listings").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/crm/listings", {
        ...form,
        price: Number(form.price || 0),
        bedrooms: Number(form.bedrooms || 0),
        bathrooms: Number(form.bathrooms || 0),
        sqft: Number(form.sqft || 0),
        images: form.images ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setOpen(false);
      setForm({ title: "", category: "residential", price: "", location: "", address: "", bedrooms: "", bathrooms: "", sqft: "", description: "", images: "", featured: false, status: "active" });
      load();
    } catch {}
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/crm/listings/${id}`, { status });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete listing?")) return;
    await api.delete(`/crm/listings/${id}`);
    load();
  };

  return (
    <CRMLayout>
      <div className="flex items-end justify-between mb-6" data-testid="crm-listings-page">
        <div>
          <div className="overline text-gold">Inventory</div>
          <h1 className="font-display text-3xl text-navy mt-2">Listings</h1>
        </div>
        <button onClick={() => setOpen(true)} className="btn-navy text-sm py-2.5 px-4" data-testid="listings-add-btn"><Plus className="w-4 h-4" /> New Listing</button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Location</th>
              <th className="p-3 text-left">Price</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} className="border-b border-slate-100" data-testid={`crm-listing-${l.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-100 overflow-hidden flex-shrink-0">
                      <img src={l.images?.[0]} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="font-semibold text-navy">{l.title}</div>
                  </div>
                </td>
                <td className="p-3 capitalize">{l.category}</td>
                <td className="p-3">{l.location}</td>
                <td className="p-3">{formatPrice(l.price)}</td>
                <td className="p-3">
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="text-xs border border-slate-200 px-2 py-1 bg-white" data-testid={`listing-status-${l.id}`}>
                    <option value="active">Active</option><option value="pending">Pending</option><option value="sold">Sold</option><option value="leased">Leased</option>
                  </select>
                </td>
                <td className="p-3"><button onClick={() => del(l.id)} className="text-xs text-red-500" data-testid={`listing-delete-${l.id}`}>Delete</button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No listings.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-navy/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-2xl p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400" data-testid="listing-modal-close"><X className="w-5 h-5" /></button>
            <div className="overline text-gold">New</div>
            <h2 className="font-display text-2xl text-navy mt-2 mb-6">Add Listing</h2>
            <form onSubmit={create} className="space-y-3" data-testid="listing-create-form">
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-title" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm bg-white" data-testid="listing-form-category">
                  <option value="residential">Residential</option><option value="commercial">Commercial</option><option value="dental">Dental</option><option value="medical">Medical</option>
                </select>
                <input required type="number" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-price" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Location (City, State)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-location" />
                <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-address" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input type="number" placeholder="Bedrooms" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-beds" />
                <input type="number" placeholder="Bathrooms" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-baths" />
                <input type="number" placeholder="Sqft" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-sqft" />
              </div>
              <textarea required rows="3" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-desc" />
              <input placeholder="Image URLs (comma-separated)" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm" data-testid="listing-form-images" />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="listing-form-featured" /> Featured on home page
              </label>
              <button className="btn-navy w-full" data-testid="listing-form-submit">Create Listing</button>
            </form>
          </div>
        </div>
      )}
    </CRMLayout>
  );
};

export default CRMListings;
